/**
 * AI:
 * Status: "in progress"
 * Type: Page
 * SubType: DetailPage
 * Reason: Detail page for a single LogicFlowTrace scoped to a parent LogicFlow.
 *         Reads multi-param route (flowId + traceId), loads the trace header
 *         and step rows via QL, maps them to the shape consumed by the
 *         existing LgFlowVariableStorePanelComponent, and hosts the trace
 *         summary + counter widget.
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';

import {PageComponent} from '../../../../core/shared/mvs-page/page.component';
import {MvsCoreService} from '../../../../core/shared/service/mvs-core.service';
import {PageHeaderConfig} from '../../../../core/shared/service/page/page-header-config.interface';
import {WidgetData} from '../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../core/shared/misc/sorting';
import {ObjectRequestList} from '../../../../core/shared/dto/object/request/object-request-list';
import {
    MvsConfigHeaderComponent
} from '../../../../core/shared/components/mvs-config-header/mvs-config-header.component';

import {LogicFlowTraceDto} from '../../model/dto/entity/logic-flow-trace.dto';
import {LogicFlowTraceStepDto} from '../../model/dto/entity/logic-flow-trace-step.dto';

@Component({
    selector: 'lg-flow-trace-detail-page',
    templateUrl: './lg-flow-trace-detail.page.html',
    styleUrls: ['./lg-flow-trace-detail.page.scss'],
    standalone: false
})
export class LgFlowTraceDetailPage extends PageComponent implements OnInit, OnDestroy, OnChanges {

    // ── Route params ──
    protected flowId: number;
    protected traceId: number;

    // ── Loaded data ──
    protected trace: LogicFlowTraceDto;
    protected traceFlowInputs: any = {};
    protected traceFlowOutputs: any = {};
    protected traceStepTraces: any[] = [];

    // ── Widgets ──
    protected counterWidget: WidgetData;

    // ── Subscriptions ──
    private paramsSubscription: Subscription;
    private traceSubscription: Subscription;
    private stepsSubscription: Subscription;

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        this.defaultLabel = 'Trace-Detail';
        super.ngOnInit();

        this.paramsSubscription = this.route.params.subscribe(params => {
            const flowIdRaw = params['flowId'];
            const traceIdRaw = params['traceId'];

            if (flowIdRaw != null) {
                this.flowId = +flowIdRaw;
            }
            if (traceIdRaw != null) {
                this.traceId = +traceIdRaw;
            }

            this.loadTrace();
            this.loadTraceSteps();
            this.refreshCounterWidget();
        });
    }

    /**
     * Load the LogicFlowTrace header for the current `traceId`. The
     * `flowInputsJson` / `flowOutputsJson` columns are parsed into the maps
     * consumed by `LgFlowVariableStorePanelComponent`.
     */
    private loadTrace(): void {
        if (this.traceId == null) {
            return;
        }

        this.traceSubscription?.unsubscribe();

        const crudService = this.coreService.getCrudService('lg.LogicFlowTrace');
        this.traceSubscription = crudService.get(this.traceId).subscribe({
            next: (result: any) => {
                this.trace = result as LogicFlowTraceDto;
                this.traceFlowInputs = this.safeParseJson(this.trace?.flowInputsJson);
                this.traceFlowOutputs = this.safeParseJson(this.trace?.flowOutputsJson);
            },
            error: () => {
                this.trace = null;
                this.traceFlowInputs = {};
                this.traceFlowOutputs = {};
            }
        });
    }

    /**
     * Load all `LogicFlowTraceStep` rows for the current trace, sorted by
     * `executionOrder` ascending, and map them into the shape expected by
     * `LgFlowVariableStorePanelComponent` (see the plan section 5 step 3
     * mapper for the canonical shape).
     */
    private loadTraceSteps(): void {
        if (this.traceId == null) {
            return;
        }

        this.stepsSubscription?.unsubscribe();

        const objectRequest = ObjectRequestList.createBasic(
            true,
            [FilterCriteria.create('trace', FilterCriteria.cOperatorEqual, this.traceId)],
            [new Sorting('executionOrder', true)]
        );

        const crudService = this.coreService.getCrudService('lg.LogicFlowTraceStep');
        this.stepsSubscription = crudService.list(objectRequest).subscribe({
            next: (result: any) => {
                const steps: LogicFlowTraceStepDto[] = (result?.entries ?? []) as LogicFlowTraceStepDto[];
                this.traceStepTraces = steps.map(s => ({
                    stepId: s.flowStepDtoId,
                    stepName: s.flowStepDto?.name ?? s.flowStepDtoName,
                    stepOrder: s.flowStepDto?.orderPriority,
                    stepType: s.flowStepDto?.stepType,
                    executionOrder: s.executionOrder,
                    status: s.stepExecutionStatus,
                    durationMs: s.durationMs,
                    inputs: this.safeParseJson(s.inputDataJson),
                    outputs: this.safeParseJson(s.outputDataJson),
                    errorMessage: s.errorMessage,
                    conditionResult: s.conditionResult,
                    failedExpectations: []
                }));
            },
            error: () => {
                this.traceStepTraces = [];
            }
        });
    }

    /**
     * Defensive JSON parser. Returns `{}` on null / empty / invalid input so
     * the variable matrix builder always sees a well-formed object.
     */
    private safeParseJson(raw: string | null | undefined): any {
        if (raw == null || raw === '') {
            return {};
        }
        try {
            const parsed = JSON.parse(raw);
            return parsed ?? {};
        } catch {
            return {};
        }
    }

    /**
     * Build the per-Logic counter widget for the current trace.
     * Filtered to the trace via the QL path on the ManyToOne `trace` field
     * (`e.traceId`). Sorted by invocation count descending so the most-used
     * logics appear first.
     */
    refreshCounterWidget(): void {
        if (this.traceId == null) {
            return;
        }

        /** AI: Status: "in progress", Type: Widget, SubType: Runtime, Reason: Table widget listing LogicFlowTraceLogicCounter rows for the current trace, sorted by invocation count descending. */
        this.counterWidget = WidgetFactory.createWidgetTableEntityQl(
            'lg.flow.logicFlowTraceCounter.table.perLogic',
            'Logik-Zähler',
            'lg.LogicFlowTraceLogicCounter',
            'Keine Zähler-Daten',
            [FilterCriteria.create('e.traceId', FilterCriteria.cOperatorEqual, this.traceId)],
            [new Sorting('e.invocationCount', false)]
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.paramsSubscription?.unsubscribe();
        this.traceSubscription?.unsubscribe();
        this.stepsSubscription?.unsubscribe();
    }

    /**
     * Page header is rendered via the centralized page header system. We use
     * the shared `MvsConfigHeaderComponent` (same pattern as other LG pages)
     * with the configuration category visual.
     */
    getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'Trace-Detail',
                categoryTypeMode: 'configuration',
                icon: 'fa-regular fa-bug'
            }
        };
    }
}
